<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div v-if="userData.permissions['view_reports'] == 1" class="row">
            <div class="col-12">
                <div class="card reports-hero-card mb-3">
                    <div class="card-body">
                        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <div>
                                <h5 class="mb-1">Report Center</h5>
                                <p class="text-muted mb-0">Browse reports by category or search by report name.</p>
                            </div>
                            <div class="reports-search-wrap">
                                <i class="mdi mdi-magnify reports-search-icon"></i>
                                <input v-model.trim="search" type="search" class="form-control reports-search"
                                    placeholder="Search reports..." />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-for="group in visibleGroups" :key="group.key" class="col-12 col-xl-3 mb-3">
                <div class="card h-100 report-group-card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <h5 class="mb-0">{{ group.title }}</h5>
                            <span class="badge bg-soft-primary text-primary">{{ group.items.length }}</span>
                        </div>

                        <p class="text-muted mb-3">{{ group.description }}</p>

                        <div class="list-group list-group-flush">
                            <router-link v-for="report in group.items" :key="report.link" :to="report.link"
                                class="list-group-item list-group-item-action report-link">
                                <span>{{ report.label }}</span>
                                <i class="mdi mdi-arrow-right"></i>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!visibleGroups.length" class="col-12">
                <div class="card">
                    <div class="card-body text-center py-4">
                        <i class="mdi mdi-file-search-outline text-muted" style="font-size: 2rem;"></i>
                        <p class="text-muted mb-0 mt-2">No reports found for "{{ search }}".</p>
                    </div>
                </div>
            </div>
        </div>

        <div v-else>
            <div class="row justify-content-center">
                <div class="col-md-8 col-xl-6">
                    <div>
                        <img src="@/assets/images/profile-img.png" alt class="img-fluid" /><br /><br />
                        <center><span class="text-primary h6">You have no permission to view {{ title }}</span></center>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";

export default {
    page: {
        title: "Reports",
        meta: [{ name: "description", content: appConfig.description }],
    },
    components: {
        Layout,
        PageHeader,
    },
    data() {
        return {
            userData: JSON.parse(localStorage.getItem("user")),
            title: "Reports",
            items: [{ text: "Dashboard", to: "/" }, { text: "Reports", active: true }],
            search: "",
            reportGroups: [
                {
                    key: "savings",
                    title: "Saving Reports",
                    description: "Track member savings, withdrawals, and share activity trends.",
                    permission: "savings_report",
                    items: [
                        { label: "Savings Per Member", link: "/savings-per-member" },
                        { label: "Withdraws Per Member", link: "/withdraws-per-member" },
                        { label: "Shares Per Member", link: "/shares-per-member" },
                        { label: "Monthly Savings Analysis", link: "/monthly-savings-analysis" },
                        { label: "Monthly Withdraws Analysis", link: "/monthly-withdraws-analysis" },
                        { label: "Monthly Shares Analysis", link: "/monthly-share-analysis" },
                    ],
                },
                {
                    key: "loans",
                    title: "Loan Reports",
                    description: "Review repayment performance, due loans, and loan product behavior.",
                    permission: "loans_report",
                    items: [
                        { label: "Loans Per Member", link: "/loans-per-member" },
                        { label: "Loan Payment Reports", link: "/loan-payment-report" },
                        { label: "Written Off Loans", link: "/written-off-loans" },
                        { label: "Due Loans", link: "/due-loans" },
                        { label: "Loans Per Loan Products", link: "/loan-per-products" },
                        { label: "Monthly Loan Analysis", link: "/loan-monthly-report" },
                    ],
                },
                {
                    key: "performance",
                    title: "Performance Reports",
                    description: "Analyze staff and transaction performance across key operations.",
                    permission: "performance_reports",
                    items: [
                        { label: "Savings Per User", link: "/savings-per-user" },
                        { label: "Withdraws Per User", link: "/withdraws-per-user" },
                        { label: "Shares Per User", link: "/shares-per-user" },
                        { label: "Loans Per User", link: "/loans-per-user" },
                        { label: "Transactions Report", link: "/transactions-report" },
                        { label: "Expenses Report", link: "/expenses-report" },
                    ],
                },
                {
                    key: "other",
                    title: "Other Reports",
                    description: "Access compliance, fee, and income and expenditure reporting.",
                    permission: "other_reports",
                    items: [
                        { label: "Inactive Member Accounts", link: "/inactive-accounts" },
                        { label: "Withdraw Charges Report", link: "/withdraw-charges-report" },
                        { label: "Loan Interests Report", link: "/loan-interests-report" },
                        { label: "Loan Charges Report", link: "/loan-charges-report" },
                        { label: "Membership Fees Report", link: "/membership-fees-port" },
                        { label: "Income & Expenditure", link: "/income-and-expenditure" },
                    ],
                },
            ],
        };
    },
    computed: {
        visibleGroups() {
            const normalizedSearch = this.search.toLowerCase();

            return this.reportGroups
                .filter((group) => this.userData.permissions[group.permission] == 1)
                .map((group) => {
                    const filteredItems = group.items.filter((item) => {
                        if (!normalizedSearch) {
                            return true;
                        }

                        return item.label.toLowerCase().includes(normalizedSearch);
                    });

                    return {
                        ...group,
                        items: filteredItems,
                    };
                })
                .filter((group) => group.items.length > 0);
        },
    },
};
</script>

<style lang="scss" scoped>
.reports-hero-card {
    border: 1px solid #e3ebf3;
}

.reports-search-wrap {
    position: relative;
    min-width: 280px;
    max-width: 360px;
    width: 100%;
}

.reports-search {
    padding-left: 2.2rem;
    border-radius: 999px;
}

.reports-search-icon {
    position: absolute;
    left: 0.8rem;
    top: 50%;
    transform: translateY(-50%);
    color: #7a8ca5;
}

.report-group-card {
    border: 1px solid #e3ebf3;
}

.report-link {
    border: 0;
    border-radius: 0.6rem;
    margin-bottom: 0.3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
}

.report-link:hover {
    background: #f3f8ff;
}
</style>
